import { useEffect, useRef } from 'react';

declare global {
  interface Window {
    adsbygoogle: unknown[];
  }
}

interface AdBannerProps {
  adSlot: string;
  adFormat?: 'auto' | 'fluid' | 'rectangle' | 'horizontal' | 'vertical';
  fullWidthResponsive?: boolean;
  className?: string;
}

/**
 * Google AdSense 배너 광고 컴포넌트
 * 
 * 사용 전 AdSense 승인이 필요합니다.
 * 승인 전에는 테스트 모드로 동작합니다.
 */
export default function AdBanner({ 
  adSlot, 
  adFormat = 'auto',
  fullWidthResponsive = true,
  className = ''
}: AdBannerProps) {
  const adRef = useRef<HTMLModElement>(null);
  const isLoaded = useRef(false);

  useEffect(() => {
    // 이미 로드된 경우 스킵
    if (isLoaded.current) return;

    try {
      // AdSense 스크립트가 로드되었는지 확인
      if (typeof window !== 'undefined' && window.adsbygoogle) {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
        isLoaded.current = true;
      }
    } catch (error) {
      console.error('AdSense error:', error);
    }
  }, []);

  // 개발 환경에서는 플레이스홀더 표시
  if (import.meta.env.DEV) {
    return (
      <div className={`bg-slate-700/50 border-2 border-dashed border-slate-500 rounded-lg p-4 text-center ${className}`}>
        <p className="text-slate-400 text-sm">📢 광고 영역</p>
        <p className="text-slate-500 text-xs mt-1">AdSlot: {adSlot}</p>
      </div>
    );
  }

  return (
    <div className={className}>
      <ins
        ref={adRef}
        className="adsbygoogle"
        style={{ display: 'block' }}
        data-ad-client={import.meta.env.VITE_ADSENSE_CLIENT_ID || 'ca-pub-2200479749872846'}
        data-ad-slot={adSlot}
        data-ad-format={adFormat}
        data-full-width-responsive={fullWidthResponsive ? 'true' : 'false'}
      />
    </div>
  );
}
